/**
 * CR307: Data Source Badge Component
 * 
 * Reality badge that shows whether viewing LIVE or SAMPLE data,
 * with publish metadata for LIVE mode.
 */

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Database, TestTube2, Clock, Hash, Layers } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import type { PublishState, DealCardsDataSource } from "@/hooks/useDealCardsData";
import { cn } from "@/lib/utils";

interface DataSourceBadgeProps {
  source: DealCardsDataSource;
  publishState?: PublishState | null;
  dealCount?: number;
  /** Compact mode for inline usage */
  compact?: boolean;
  className?: string;
}

export function DataSourceBadge({
  source,
  publishState,
  dealCount,
  compact = false,
  className,
}: DataSourceBadgeProps) {
  if (source === "live") {
    return (
      <div className={cn("flex items-center gap-2 flex-wrap", className)}>
        {/* Primary LIVE badge */}
        <Badge 
          variant="default" 
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
        >
          <Database className="h-3 w-3 mr-1" />
          LIVE
        </Badge>

        {!compact && publishState && (
          <>
            {/* Last published */}
            {publishState.lastPublishedAt && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                {formatDistanceToNow(new Date(publishState.lastPublishedAt), { addSuffix: true })}
              </Badge>
            )}

            {/* Row count */}
            {publishState.lastPublishedRowCount > 0 && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                <Layers className="h-3 w-3 mr-1" />
                {publishState.lastPublishedRowCount} rows
              </Badge>
            )}

            {/* Snapshot ID (truncated) */}
            {publishState.activeSnapshotId && (
              <Badge variant="outline" className="text-xs font-mono text-muted-foreground">
                <Hash className="h-3 w-3 mr-1" />
                {publishState.activeSnapshotId.slice(0, 8)}
              </Badge>
            )}
          </>
        )}

        {/* Deal count if provided */}
        {dealCount !== undefined && compact && (
          <span className="text-xs text-muted-foreground">
            ({dealCount})
          </span>
        )}
      </div>
    );
  }

  // SAMPLE mode
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Badge 
        variant="secondary" 
        className="bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium border-border"
      >
        <TestTube2 className="h-3 w-3 mr-1" />
        SAMPLE
      </Badge>

      {!compact && (
        <Badge variant="outline" className="text-xs text-muted-foreground">
          v_layout_studio_sample_deals
        </Badge>
      )}

      {dealCount !== undefined && compact && (
        <span className="text-xs text-muted-foreground">
          ({dealCount})
        </span>
      )}
    </div>
  );
}

// ================================================================
// DATA SOURCE TOGGLE
// ================================================================

interface DataSourceToggleProps {
  source: DealCardsDataSource;
  onSourceChange: (source: DealCardsDataSource) => void;
  disabled?: boolean;
  className?: string;
}

export function DataSourceToggle({
  source,
  onSourceChange,
  disabled = false,
  className,
}: DataSourceToggleProps) {
  return (
    <div className={cn("inline-flex rounded-md border border-input bg-background", className)}>
      <button
        type="button"
        onClick={() => onSourceChange("live")}
        disabled={disabled}
        className={cn(
          "px-3 py-1.5 text-sm font-medium rounded-l-md transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
          source === "live"
            ? "bg-primary text-primary-foreground"
            : "bg-transparent text-muted-foreground hover:bg-muted",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <Database className="h-3.5 w-3.5 inline mr-1.5" />
        Live
      </button>
      <button
        type="button"
        onClick={() => onSourceChange("sample")}
        disabled={disabled}
        className={cn(
          "px-3 py-1.5 text-sm font-medium rounded-r-md transition-colors border-l border-input",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
          source === "sample"
            ? "bg-secondary text-secondary-foreground"
            : "bg-transparent text-muted-foreground hover:bg-muted",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <TestTube2 className="h-3.5 w-3.5 inline mr-1.5" />
        Sample
      </button>
    </div>
  );
}

export default DataSourceBadge;
